import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  AppBar,
  Typography,
  IconButton,
  Paper,
  Divider,
  Chip,
  Button
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Map as MapIcon,
  Devices as DevicesIcon,
  Category as CategoryIcon,
  ListAlt as ListAltIcon,
  LocationOn as LocationIcon,
  AccountTree as AccountTreeIcon,
  Logout as LogoutIcon,
  Settings as SettingsIcon,
  Warning as WarningIcon,
  Menu as MenuIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Notifications as NotificationsIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  History as HistoryIcon,
  Apps as AppsIcon,
  Storage as StorageIcon,
  Assignment as AssignmentIcon,
  Inventory2 as AssetIcon,
  Warehouse as WarehouseMenuIcon,
  ReportProblem as ProblemIcon,
  Assessment as AnalyticsIcon,
  FactCheck as ChecksIcon,
  NetworkCheck as MonitoringIcon,
  Hub as IntegrationsIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import RequiredFieldsReminder from './RequiredFieldsReminder';
import CommandPalette from './CommandPalette';

const drawerWidth = 240;
const rightPanelWidth = 280;

const menuItems = [
  { text: 'Главная', icon: <DashboardIcon />, path: '/' },
  { text: 'Карта', icon: <MapIcon />, path: '/map', permission: 'map.view' },
  { text: 'Карта 2.0 (авто)', icon: <MapIcon />, path: '/map-auto', permission: 'map.view' },
  { text: 'Карта 3.0', icon: <MapIcon />, path: '/map3', permission: 'map.view' },
  { text: 'Карта 4.0', icon: <MapIcon />, path: '/map4', permission: 'map.view' },
  { text: 'Устройства', icon: <DevicesIcon />, path: '/devices', permission: 'devices.view' },
  { text: 'Здоровье системы', icon: <HistoryIcon />, path: '/health' },
  { text: 'Аналитика', icon: <AnalyticsIcon />, path: '/analytics', permission: 'devices.view' },
  { divider: true, text: 'Конфигурация' },
  { text: 'Характеристики', icon: <ListAltIcon />, path: '/characteristics', permission: 'characteristics.view' },
  { text: 'Типы устройств', icon: <AccountTreeIcon />, path: '/device-types', permission: 'device_types.view' },
  { text: 'Локации', icon: <LocationIcon />, path: '/locations', permission: 'locations.view' },
  { text: 'Типы подключений', icon: <CategoryIcon />, path: '/connection-types', permission: 'connection_types.view' },
  { text: 'Каталог ПО', icon: <AppsIcon />, path: '/software-catalog', permission: 'software.view' },
  { text: 'Пользователи устройств', icon: <PeopleIcon />, path: '/device-users', permission: 'device_users.view' },
  { divider: true, text: 'Сеть' },
  { text: 'Подключения', icon: <DevicesIcon />, path: '/connections', permission: 'connections.view' },
  { text: 'IP адреса', icon: <LanguageIcon />, path: '/subnets', permission: 'subnets.view' },
  { text: 'Матрицы', icon: <AccountTreeIcon />, path: '/matrices' },
  { text: 'Матрица доступности', icon: <LanguageIcon />, path: '/network-matrix', permission: 'devices.view' },
  { text: 'Скачать агент', icon: <AppsIcon />, path: '/agent-download', permission: 'devices.view' },
  { divider: true, text: 'Управление' },
  { text: 'Заявки на изменение', icon: <AssignmentIcon />, path: '/change-requests', permission: 'change_requests.view' },
  { text: 'Дрейф изменений', icon: <WarningIcon />, path: '/drift', permission: 'drift.view' },
  { text: 'Активы', icon: <AssetIcon />, path: '/assets', permission: 'assets.view' },
  { text: 'Склад', icon: <WarehouseMenuIcon />, path: '/warehouse', permission: 'warehouse.view' },
  { text: 'Проблемы', icon: <ProblemIcon />, path: '/issues', permission: 'issues.view' },
  { text: 'Системные проверки', icon: <ChecksIcon />, path: '/system-checks', permission: 'issues.view' },
  { text: 'Мониторинг сети', icon: <MonitoringIcon />, path: '/monitoring', permission: 'monitoring.view' },
  { divider: true, text: 'Администрирование' },
  { text: 'Интеграции', icon: <IntegrationsIcon />, path: '/integrations', permission: 'integrations.view' },
  { text: 'Пользователи', icon: <PeopleIcon />, path: '/users', permission: 'users.view' },
  { text: 'Роли и права', icon: <SecurityIcon />, path: '/roles', permission: 'roles.view' },
  { text: 'История изменений', icon: <HistoryIcon />, path: '/audit-logs', permission: 'audit.view' },
  { text: 'База данных', icon: <StorageIcon />, path: '/database', superuserOnly: true },
  { divider: true, text: 'Аккаунт' },
  { text: 'Мой профиль', icon: <PersonIcon />, path: '/profile' },
];

function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();

  // State for drawer visibility - load from localStorage
  const [leftDrawerOpen, setLeftDrawerOpen] = useState(() => {
    const saved = localStorage.getItem('leftDrawerOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [rightDrawerOpen, setRightDrawerOpen] = useState(() => {
    const saved = localStorage.getItem('rightDrawerOpen');
    return saved !== null ? JSON.parse(saved) : true;
  });

  const [stats, setStats] = useState({
    devices: 0,
    deviceTypes: 0,
    locations: 0
  });

  const [incompleteDevicesCount, setIncompleteDevicesCount] = useState(0);
  const [govNotifs, setGovNotifs] = useState([]);

  // Governance inbox (заявки/дрейф/склад по правам пользователя)
  useEffect(() => {
    axios.get('/notifications/').then((r) => setGovNotifs(r.data.items || [])).catch(() => {});
  }, []);

  // Notification for incomplete devices
  const notifications = incompleteDevicesCount > 0 ? [
    {
      id: 'incomplete-devices',
      type: 'warning',
      count: incompleteDevicesCount,
      message: `Устройств с незаполненными обязательными полями: ${incompleteDevicesCount}`
    }
  ] : [];

  // Save drawer state to localStorage
  useEffect(() => {
    localStorage.setItem('leftDrawerOpen', JSON.stringify(leftDrawerOpen));
  }, [leftDrawerOpen]);

  useEffect(() => {
    localStorage.setItem('rightDrawerOpen', JSON.stringify(rightDrawerOpen));
  }, [rightDrawerOpen]);

  // Save current route to localStorage
  useEffect(() => {
    localStorage.setItem('lastRoute', location.pathname);
  }, [location.pathname]);

  // Load statistics
  useEffect(() => {
    const loadStats = async () => {
      try {
        const [devicesRes, typesRes, locsRes, incompleteRes] = await Promise.all([
          fetch('/api/devices/'),
          fetch('/api/device-types/'),
          fetch('/api/locations/'),
          fetch('/api/devices/incomplete/count')
        ]);

        const devices = await devicesRes.json();
        const types = await typesRes.json();
        const locs = await locsRes.json();
        const incomplete = await incompleteRes.json();

        setStats({
          devices: devices.total || devices.length || 0,
          deviceTypes: types.length || 0,
          locations: locs.length || 0
        });

        setIncompleteDevicesCount(incomplete.count || 0);
      } catch (err) {
        console.error('Failed to load stats:', err);
      }
    };

    loadStats();
  }, []);

  // Toggle handlers
  const handleLeftDrawerToggle = () => {
    setLeftDrawerOpen(!leftDrawerOpen);
  };

  const handleRightDrawerToggle = () => {
    setRightDrawerOpen(!rightDrawerOpen);
  };

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: '#1976d2'
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            onClick={handleLeftDrawerToggle}
            edge="start"
            sx={{ mr: 2 }}
          >
            {leftDrawerOpen ? <ChevronLeftIcon /> : <MenuIcon />}
          </IconButton>
          <SettingsIcon sx={{ mr: 2 }} />
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            Inventory System
          </Typography>
          <Typography variant="body2" sx={{ mr: 2 }}>
            {user?.email}
          </Typography>
          <IconButton
            color="inherit"
            onClick={handleRightDrawerToggle}
            sx={{ mr: 1 }}
          >
            {rightDrawerOpen ? <ChevronRightIcon /> : <NotificationsIcon />}
          </IconButton>
          <IconButton color="inherit" onClick={logout}>
            <LogoutIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Left Sidebar */}
      <Drawer
        variant="permanent"
        open={leftDrawerOpen}
        sx={{
          width: leftDrawerOpen ? drawerWidth : 0,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: leftDrawerOpen ? drawerWidth : 0,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: leftDrawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto' }}>
          <List>
            {menuItems.map((item, index) => {
              // Check superuser-only items
              if (item.superuserOnly && !user?.is_superuser) {
                return null;
              }

              // Check permission for menu item
              if (item.permission && !user?.is_superuser) {
                const hasPermission = user?.permissions?.includes(item.permission);
                if (!hasPermission) return null;
              }

              if (item.divider) {
                // Check if there are visible items after this divider
                const nextItems = menuItems.slice(index + 1);
                const hasVisibleNext = nextItems.some((next, idx) => {
                  if (next.divider) return false;
                  if (!next.permission) return true;
                  if (user?.is_superuser) return true;
                  return user?.permissions?.includes(next.permission);
                });

                // Only show divider if there are visible items after it
                if (!hasVisibleNext) return null;

                return (
                  <Box key={index}>
                    <Divider sx={{ my: 1 }} />
                    <ListItem>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        {item.text}
                      </Typography>
                    </ListItem>
                  </Box>
                );
              }

              const isActive = location.pathname === item.path;

              return (
                <ListItem key={item.path} disablePadding>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => {
                      if (item.path === '/') {
                        // Clear lastRoute and navigate to dashboard
                        localStorage.setItem('lastRoute', '/');
                        navigate('/', { state: { refresh: Date.now() } });
                      } else if (isActive) {
                        // If already on this page, force reload
                        window.location.reload();
                      } else {
                        navigate(item.path);
                      }
                    }}
                  >
                    <ListItemIcon sx={{ color: isActive ? 'primary.main' : 'inherit' }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText primary={item.text} />
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          transition: (theme) => theme.transitions.create(['margin', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          marginLeft: 0,
          marginRight: 0,
          width: `calc(100% - ${leftDrawerOpen ? drawerWidth : 0}px - ${rightDrawerOpen ? rightPanelWidth : 0}px)`
        }}
      >
        <Toolbar />
        {children}
        <RequiredFieldsReminder />
        <CommandPalette />
      </Box>

      {/* Right Notifications Panel */}
      <Drawer
        variant="permanent"
        anchor="right"
        open={rightDrawerOpen}
        sx={{
          width: rightDrawerOpen ? rightPanelWidth : 0,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          boxSizing: 'border-box',
          '& .MuiDrawer-paper': {
            width: rightDrawerOpen ? rightPanelWidth : 0,
            boxSizing: 'border-box',
            overflowX: 'hidden',
            transition: (theme) => theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: rightDrawerOpen
                ? theme.transitions.duration.enteringScreen
                : theme.transitions.duration.leavingScreen,
            }),
          },
        }}
      >
        <Toolbar />
        <Box sx={{ overflow: 'auto', p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Уведомления
          </Typography>

          {govNotifs.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 1 }}>
              {govNotifs.map((n) => (
                <Paper key={n.kind} sx={{ p: 1.5, borderLeft: 4, borderColor: `${n.severity}.main` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">{n.title}</Typography>
                    <Chip label={n.count} size="small" color={n.severity} />
                  </Box>
                  <Button size="small" sx={{ mt: 0.5 }} onClick={() => navigate(n.link)}>Открыть</Button>
                </Paper>
              ))}
            </Box>
          )}

          {notifications.length === 0 && govNotifs.length === 0 ? (
            <Paper sx={{ p: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Нет уведомлений
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {notifications.map((notif) => (
                <Paper
                  key={notif.id}
                  sx={{
                    p: 2,
                    borderLeft: 4,
                    borderColor: notif.type === 'warning' ? 'warning.main' : 'info.main'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <WarningIcon
                      sx={{
                        mr: 1,
                        color: notif.type === 'warning' ? 'warning.main' : 'info.main'
                      }}
                      fontSize="small"
                    />
                    <Chip
                      label={notif.count}
                      size="small"
                      color={notif.type === 'warning' ? 'warning' : 'info'}
                    />
                  </Box>
                  <Typography variant="body2">
                    {notif.message}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate('/incomplete-devices')}
                  >
                    Просмотр
                  </Button>
                </Paper>
              ))}
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Статистика
          </Typography>
          <Paper sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Всего устройств:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {stats.devices}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Типов устройств:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {stats.deviceTypes}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Локаций:
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {stats.locations}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Drawer>
    </Box>
  );
}

export default Layout;
